import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { TEAMS_QUERY_KEY } from '../constants';
import { deactivateUser } from '../services';
import { DeactivateUserContext, DeactivateUserVariables, TeamListItem, UserStatuses } from '../types';
import { teamListCompare } from '../utils';
import { FETCH_DELAY } from '@modules/shared/constants';

function useMutationDeactivateUser(
  onSuccess: (data: any, variables: DeactivateUserVariables, context: DeactivateUserContext | undefined) => void,
  onError?: () => void,
): UseMutationResult<string, unknown, DeactivateUserVariables, DeactivateUserContext> {
  const queryClient = useQueryClient();

  const mutationFn = async (id: any): Promise<any> => {
    await deactivateUser(id);
  };

  const onMutateFn = async (id: DeactivateUserVariables): Promise<DeactivateUserContext> => {
    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({ queryKey: [TEAMS_QUERY_KEY] });

    const previousUsers = queryClient.getQueryData([TEAMS_QUERY_KEY]) as TeamListItem[];

    // Optimistically update to the new value
    queryClient.setQueryData([TEAMS_QUERY_KEY], (old: TeamListItem[] | undefined): TeamListItem[] | undefined => {
      if (old === undefined) {
        return [];
      }

      const oldData = [...old];

      const index = oldData.findIndex((t) => t.id === (id as unknown));

      oldData[index] = {
        ...oldData[index],
        status: UserStatuses.DEPROVISIONED,
      };

      oldData.sort(teamListCompare);

      return oldData;
    });

    // Return a context object with the snapshotted value
    return { previousUsers };
  };

  const onErrorFn = (_err: unknown, _id: string, context: DeactivateUserContext | undefined): void => {
    if (context === undefined) {
      return;
    }
    if (context.previousUsers !== undefined) {
      queryClient.setQueryData([TEAMS_QUERY_KEY], context.previousUsers);
    }
    onError?.();
  };

  const onSettledFn = async (): Promise<void> => {
    setTimeout(() => {
      void queryClient.invalidateQueries([TEAMS_QUERY_KEY]);
    }, FETCH_DELAY);
  };

  return useMutation<string, unknown, DeactivateUserVariables, DeactivateUserContext>(['deactivateUser'], mutationFn, {
    onMutate: onMutateFn,
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: onErrorFn,
    // Always refetch after error or success:
    onSettled: onSettledFn,
    onSuccess,
  });
}

export default useMutationDeactivateUser;
